import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "@swot/base-components";

import { ApiErrorPanel } from "@components/errors/index.js";
import { useApiError } from "@hooks/useApiError.js";
import { reportApi } from "@services/reportApi.js";

type Row = Record<string, unknown>;

export type CmtsReportPreviewPanelProps = {
  readonly reportCode: string;
  readonly filters?: Row | undefined;
  readonly autoRun?: boolean | undefined;
};

type PreviewColumn = {
  readonly key: string;
  readonly label: string;
  readonly type?: "text" | "date" | "number" | "status";
};

const preferredColumns: readonly PreviewColumn[] = [
  {
    key: "complaintNumber",
    label: "Complaint Number",
    type: "text",
  },
  {
    key: "title",
    label: "Title",
    type: "text",
  },
  {
    key: "category",
    label: "Category",
    type: "text",
  },
  {
    key: "subcategory",
    label: "Subcategory",
    type: "text",
  },
  {
    key: "priority",
    label: "Priority",
    type: "status",
  },
  {
    key: "severity",
    label: "Severity",
    type: "status",
  },
  {
    key: "status",
    label: "Status",
    type: "status",
  },
  {
    key: "workflowStatus",
    label: "Workflow Status",
    type: "status",
  },
  {
    key: "site",
    label: "Site",
    type: "text",
  },
  {
    key: "department",
    label: "Department",
    type: "text",
  },
  {
    key: "owner",
    label: "Owner",
    type: "text",
  },
  {
    key: "dueDate",
    label: "Due Date",
    type: "date",
  },
  {
    key: "ageingDays",
    label: "Ageing Days",
    type: "number",
  },
  {
    key: "slaStatus",
    label: "SLA Status",
    type: "status",
  },
];

export function CmtsReportPreviewPanel(
  props: CmtsReportPreviewPanelProps,
) {
  const [rows, setRows] =
    useState<readonly Row[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [lastUpdatedAt, setLastUpdatedAt] =
    useState<string | null>(null);

  const autoRunStarted =
    useRef(false);

  const apiError = useApiError();

  const visibleColumns = useMemo(
    () => findVisibleColumns(rows),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch =
      search.trim().toLowerCase();

    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((row) =>
      visibleColumns.some((column) => {
        const value = getDisplayValue(
          row,
          column.key,
        );

        return value
          .toLowerCase()
          .includes(normalizedSearch);
      }),
    );
  }, [rows, search, visibleColumns]);

  async function preview(
    signal?: AbortSignal,
  ): Promise<void> {
    if (!props.reportCode.trim()) {
      return;
    }

    setLoading(true);
    apiError.clearError();

    try {
      const result =
        await reportApi.previewReport(
          props.reportCode,
          {
            reportCode: props.reportCode,
            filters: props.filters ?? {},
            pageNumber: 1,
            pageSize: 100,
          },
          signal,
        );

      if (signal?.aborted) {
        return;
      }

      setRows(readRows(result));
      setLastUpdatedAt(
        new Date().toISOString(),
      );
    } catch (error: unknown) {
      if (signal?.aborted) {
        return;
      }

      apiError.setError(error, {
        operation: "report.preview",
        entityType: "Report",
        entityId: props.reportCode,
      });
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    autoRunStarted.current = false;
    setRows([]);
    setSearch("");
    setLastUpdatedAt(null);
    apiError.clearError();
  }, [props.reportCode]);

  useEffect(() => {
    if (
      !props.autoRun ||
      autoRunStarted.current
    ) {
      return;
    }

    autoRunStarted.current = true;

    const controller =
      new AbortController();

    void preview(controller.signal);

    return () => {
      controller.abort();
    };
  }, [
    props.autoRun,
    props.reportCode,
    JSON.stringify(props.filters ?? {}),
  ]);

  return (
    <section className="cmts-report-preview-panel">
      <header className="cmts-report-panel-header">
        <div>
          <span className="cmts-report-panel-eyebrow">
            LIVE REPORT PREVIEW
          </span>

          <h2 className="cmts-report-panel-title">
            {formatReportCode(
              props.reportCode,
            )}
          </h2>

          <p className="cmts-report-panel-description">
            Review the latest backend report data for
            the selected tenant and filters.
          </p>
        </div>

        <div className="cmts-report-preview-actions">
          <Button
            label={
              rows.length > 0
                ? "Refresh preview"
                : "Run preview"
            }
            variant="secondary"
            loading={loading}
            disabled={
              loading ||
              !props.reportCode.trim()
            }
            onClick={() =>
              void preview()
            }
          />
        </div>
      </header>

      <div className="cmts-report-preview-toolbar">
        <div className="cmts-report-preview-meta">
          <span className="cmts-report-meta-pill">
            Report: {props.reportCode}
          </span>

          <span className="cmts-report-meta-pill">
            Total rows: {rows.length}
          </span>

          <span className="cmts-report-meta-pill">
            Showing: {filteredRows.length}
          </span>

          {lastUpdatedAt ? (
            <span className="cmts-report-meta-pill">
              Updated:{" "}
              {formatDateTime(
                lastUpdatedAt,
              )}
            </span>
          ) : null}
        </div>

        <label className="cmts-report-preview-search">
          <span className="cmts-report-sr-only">
            Search report rows
          </span>

          <span
            className="cmts-report-search-icon"
            aria-hidden="true"
          >
            ⌕
          </span>

          <input
            type="search"
            value={search}
            placeholder="Search report data..."
            onChange={(event) =>
              setSearch(
                event.currentTarget.value,
              )
            }
          />

          {search ? (
            <button
              type="button"
              aria-label="Clear preview search"
              onClick={() => setSearch("")}
            >
              ×
            </button>
          ) : null}
        </label>
      </div>

      <ApiErrorPanel
        error={apiError.error}
        compact
        onRetry={() =>
          void preview()
        }
      />

      <div className="cmts-report-preview-table-card">
        {loading ? (
          <ReportLoadingState />
        ) : filteredRows.length === 0 ? (
          <ReportEmptyState
            searching={Boolean(
              search.trim(),
            )}
          />
        ) : (
          <div className="cmts-report-table-scroll">
            <table className="cmts-report-preview-table">
              <thead>
                <tr>
                  {visibleColumns.map(
                    (column) => (
                      <th
                        key={column.key}
                        scope="col"
                      >
                        {column.label}
                      </th>
                    ),
                  )}
                </tr>
              </thead>

              <tbody>
                {filteredRows.map(
                  (row, index) => (
                    <tr
                      key={reportRowKey(
                        row,
                        index,
                      )}
                    >
                      {visibleColumns.map(
                        (column) => (
                          <td key={column.key}>
                            <ReportCell
                              row={row}
                              column={column}
                            />
                          </td>
                        ),
                      )}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function ReportCell(props: {
  readonly row: Row;
  readonly column: PreviewColumn;
}) {
  const rawValue = getResolvedValue(
    props.row,
    props.column.key,
  );

  if (
    rawValue === null ||
    rawValue === undefined ||
    rawValue === ""
  ) {
    return (
      <span className="cmts-report-empty-value">
        —
      </span>
    );
  }

  /*
   * A GUID is an internal database ID.
   * Do not display it as a business value.
   */
  if (
    typeof rawValue === "string" &&
    isGuid(rawValue)
  ) {
    return (
      <span
        className="cmts-report-unresolved-value"
        title="Backend must return the corresponding name or code."
      >
        Value unavailable
      </span>
    );
  }

  if (props.column.type === "date") {
    return (
      <span>
        {formatDateTime(
          String(rawValue),
        )}
      </span>
    );
  }

  if (props.column.type === "status") {
    const value = String(rawValue);

    return (
      <span
        className={statusClassName(value)}
      >
        {formatBusinessValue(value)}
      </span>
    );
  }

  if (typeof rawValue === "boolean") {
    return (
      <span>
        {rawValue ? "Yes" : "No"}
      </span>
    );
  }

  return (
    <span>
      {formatBusinessValue(
        String(rawValue),
      )}
    </span>
  );
}

function ReportLoadingState() {
  return (
    <div className="cmts-report-preview-state">
      <div className="cmts-report-preview-spinner" />

      <strong>
        Loading report preview
      </strong>

      <span>
        Fetching the latest report rows...
      </span>
    </div>
  );
}

function ReportEmptyState(props: {
  readonly searching: boolean;
}) {
  return (
    <div className="cmts-report-preview-state">
      <div className="cmts-report-empty-icon">
        {props.searching ? "⌕" : "▤"}
      </div>

      <strong>
        {props.searching
          ? "No matching report rows"
          : "No preview rows returned"}
      </strong>

      <span>
        {props.searching
          ? "Try a different search value."
          : "The backend did not return report data for the selected filters."}
      </span>
    </div>
  );
}

function readRows(
  input: unknown,
): Row[] {
  if (Array.isArray(input)) {
    return input.filter(isRecord);
  }

  if (!isRecord(input)) {
    return [];
  }

  const directRows =
    input.rows ??
    input.items ??
    input.records;

  if (Array.isArray(directRows)) {
    return directRows.filter(isRecord);
  }

  if (Array.isArray(input.data)) {
    return input.data.filter(isRecord);
  }

  if (isRecord(input.data)) {
    const nestedRows =
      input.data.rows ??
      input.data.items ??
      input.data.records ??
      input.data.data;

    if (Array.isArray(nestedRows)) {
      return nestedRows.filter(isRecord);
    }
  }

  if (isRecord(input.result)) {
    const resultRows =
      input.result.rows ??
      input.result.items ??
      input.result.records ??
      input.result.data;

    if (Array.isArray(resultRows)) {
      return resultRows.filter(isRecord);
    }
  }

  return [];
}

function findVisibleColumns(
  rows: readonly Row[],
): readonly PreviewColumn[] {
  if (rows.length === 0) {
    return preferredColumns.slice(0, 8);
  }

  const availableColumns =
    preferredColumns.filter((column) =>
      rows.some((row) =>
        hasBusinessValue(
          row,
          column.key,
        ),
      ),
    );

  return availableColumns.length > 0
    ? availableColumns
    : preferredColumns.slice(0, 8);
}

function hasBusinessValue(
  row: Row,
  key: string,
): boolean {
  const value =
    getResolvedValue(row, key);

  return (
    value !== null &&
    value !== undefined &&
    value !== ""
  );
}

/*
 * Prefer display-name properties before raw ID properties.
 *
 * Example:
 * categoryName -> categoryLabel -> categoryCode -> category
 */
function getResolvedValue(
  row: Row,
  key: string,
): unknown {
  const candidates =
    displayPropertyCandidates[key] ??
    [
      `${key}Name`,
      `${key}Label`,
      `${key}Code`,
      key,
    ];

  for (const candidate of candidates) {
    const value = row[candidate];

    if (
      value !== null &&
      value !== undefined &&
      value !== ""
    ) {
      return value;
    }
  }

  return null;
}

const displayPropertyCandidates:
  Readonly<Record<string, readonly string[]>> = {
    complaintNumber: [
      "complaintNumber",
      "displayCode",
      "complaintCode",
      "referenceNumber",
    ],

    title: [
      "title",
      "complaintTitle",
      "name",
    ],

    category: [
      "categoryName",
      "categoryLabel",
      "categoryCode",
      "complaintCategoryName",
      "category",
    ],

    subcategory: [
      "subcategoryName",
      "subcategoryLabel",
      "subcategoryCode",
      "complaintSubcategoryName",
      "subcategory",
    ],

    priority: [
      "priorityName",
      "priorityLabel",
      "priorityCode",
      "priority",
    ],

    severity: [
      "severityName",
      "severityLabel",
      "severityCode",
      "severity",
    ],

    status: [
      "statusName",
      "statusLabel",
      "statusCode",
      "complaintStatusName",
      "status",
    ],

    workflowStatus: [
      "workflowStatusName",
      "workflowStatusLabel",
      "workflowStatusCode",
      "workflowStatus",
    ],

    site: [
      "siteName",
      "siteLabel",
      "siteCode",
      "site",
    ],

    department: [
      "departmentName",
      "departmentLabel",
      "departmentCode",
      "department",
    ],

    owner: [
      "ownerDisplayName",
      "ownerName",
      "assignedToName",
      "assigneeName",
      "owner",
    ],

    dueDate: [
      "dueDate",
      "dueAt",
      "targetDate",
    ],

    ageingDays: [
      "ageingDays",
      "agingDays",
    ],

    slaStatus: [
      "slaStatusName",
      "slaStatusLabel",
      "slaStatusCode",
      "slaStatus",
    ],
  };

function getDisplayValue(
  row: Row,
  key: string,
): string {
  const value =
    getResolvedValue(row, key);

  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function reportRowKey(
  row: Row,
  index: number,
): string {
  const candidate =
    row.id ??
    row.sourceEntityId ??
    row.complaintId ??
    row.complaintNumber ??
    row.displayCode;

  if (
    typeof candidate === "string" ||
    typeof candidate === "number"
  ) {
    return String(candidate);
  }

  return `report-row-${index}`;
}

function statusClassName(
  value: string,
): string {
  const normalized =
    value.toUpperCase();

  if (
    normalized.includes("BREACH") ||
    normalized.includes("REJECT") ||
    normalized.includes("FAILED") ||
    normalized.includes("CANCEL")
  ) {
    return "cmts-report-status cmts-report-status--danger";
  }

  if (
    normalized.includes("PENDING") ||
    normalized.includes("REVIEW") ||
    normalized.includes("RISK") ||
    normalized.includes("PROGRESS")
  ) {
    return "cmts-report-status cmts-report-status--warning";
  }

  if (
    normalized.includes("COMPLETE") ||
    normalized.includes("CLOSED") ||
    normalized.includes("APPROVED") ||
    normalized.includes("OK") ||
    normalized.includes("SUCCESS")
  ) {
    return "cmts-report-status cmts-report-status--success";
  }

  return "cmts-report-status cmts-report-status--neutral";
}

function formatBusinessValue(
  input: string,
): string {
  return input
    .replace(/[_-]+/g, " ")
    .replace(
      /([a-z0-9])([A-Z])/g,
      "$1 $2",
    )
    .replace(/\s+/g, " ")
    .trim()
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase(),
    );
}

function formatReportCode(
  input: string,
): string {
  return formatBusinessValue(input);
}

function formatDateTime(
  input: string,
): string {
  const date = new Date(input);

  if (Number.isNaN(date.getTime())) {
    return input;
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    {
      dateStyle: "medium",
      timeStyle: "short",
    },
  ).format(date);
}

function isGuid(
  value: string,
): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value.trim(),
  );
}

function isRecord(
  value: unknown,
): value is Row {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}